import { useAuth } from '../contexts/AuthContext';
import { canManageCustomerAccess } from '../lib/customerAccess';
import { useMemo, useState } from 'react';
import {
  useCustomersLite,
  useCustomerUsers,
  useContactsForCustomer,
  useInviteCustomerUser,
  type CustomerUserStatus,
  type ContactLite,
  type InviteParams,
} from '../hooks/useCustomerProvisioning';
import { describeJobError } from '../lib/triggerTask';
import { portalViewAsUrl } from '../lib/portalUrl';
import { formatDateTime } from '../lib/formatters';

/**
 * Customer provisioning panel.
 *
 * Three responsibilities, in this order:
 *   1. Pick a customer by name.
 *   2. Show its current portal access roster.
 *   3. Invite a Zoho contact — primary by default, with the option to opt in
 *      additional contacts. Nothing is provisioned automatically from sync.
 *
 * Every invite is a `viewer`: `users.role` doubles as the RLS staff bypass,
 * so a customer contact is never invited with a staff-level role from here.
 */
export function ProvisioningPage() {
  const { data: customers, isLoading, isError } = useCustomersLite();
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const list = customers ?? [];
    if (!search) return list;
    const q = search.toLowerCase();
    return list.filter((c) => c.name.toLowerCase().includes(q) || c.account_number.toLowerCase().includes(q));
  }, [customers, search]);

  const selected = (customers ?? []).find((c) => c.id === selectedId) ?? null;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-text">Provisioning</h1>
        <p className="text-sm text-text-muted mt-1">
          Invite Zoho contacts to the customer portal and review who already has access.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4 lg:gap-6">
        <aside className="rounded-lg border border-border bg-surface">
          <header className="px-4 py-3 border-b border-border">
            <h2 className="text-sm font-semibold text-text">Customers</h2>
            <label className="block mt-2">
              <span className="sr-only">Search by customer name or account number</span>
              <input
                type="search"
                placeholder="Search name or account number"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full min-h-[44px] md:min-h-[40px] rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </label>
          </header>
          {isError ? (
            <p role="alert" className="p-4 text-sm text-notification">Could not load customers. Refresh to try again.</p>
          ) : (
            <ul className="max-h-[50vh] lg:max-h-[70vh] overflow-y-auto divide-y divide-border" aria-label="Customers">
              {isLoading && <li className="p-4 text-sm text-text-muted">Loading…</li>}
              {!isLoading && filtered.length === 0 && (
                <li className="p-4 text-sm text-text-muted">No customers match.</li>
              )}
              {!isLoading &&
                filtered.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(c.id)}
                      aria-pressed={selectedId === c.id}
                      className={`w-full text-left px-4 py-2.5 min-h-[44px] hover:bg-surface-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary ${
                        selectedId === c.id ? 'bg-primary-soft' : ''
                      }`}
                    >
                      <div className="text-sm font-medium text-text">{c.name}</div>
                      <div className="text-xs text-text-muted flex flex-wrap items-center gap-2 mt-0.5">
                        <span>{c.account_number}</span>
                        {c.is_zoho_synced ? (
                          <span className="rounded bg-success-soft px-1.5 py-0.5 text-success">Zoho-linked</span>
                        ) : (
                          <span className="rounded bg-warning-soft px-1.5 py-0.5 text-warning">Local only</span>
                        )}
                        {!c.portal_active && (
                          <span className="rounded bg-surface-3 px-1.5 py-0.5 text-text-muted">Portal off</span>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
            </ul>
          )}
        </aside>

        {selected ? (
          <div className="order-first lg:order-none">
            <ProvisioningPanel customerId={selected.id} customerName={selected.name} />
          </div>
        ) : (
          <div className="rounded-lg border border-border bg-surface p-8 text-sm text-text-muted">
            Select a customer to manage portal provisioning.
          </div>
        )}
      </div>
    </div>
  );
}

function ProvisioningPanel({ customerId, customerName }: { customerId: string; customerName: string }) {
  const { data: users, isLoading, isError } = useCustomerUsers(customerId);
  const { data: contacts, isLoading: contactsLoading, isError: contactsError } = useContactsForCustomer(customerId);
  const invite = useInviteCustomerUser();
  const { role } = useAuth();
  const canManage = canManageCustomerAccess(role);
  const [showAllContacts, setShowAllContacts] = useState(false);

  // Already-provisioned contact IDs so we can skip them in the eligible list.
  const provisionedContactIds = useMemo(() => {
    const s = new Set<string>();
    (users ?? []).forEach((u) => {
      if (u.contact_id) s.add(u.contact_id);
    });
    return s;
  }, [users]);

  // Default eligible set: primary contact only. Admin can opt-in additional
  // contacts by toggling "show all contacts."
  const eligible = useMemo(() => {
    const list = (contacts ?? []).filter((c) => !provisionedContactIds.has(c.id));
    return showAllContacts ? list : list.filter((c) => c.is_primary);
  }, [contacts, provisionedContactIds, showAllContacts]);

  const contactTotal = contacts?.length ?? 0;

  return (
    <section className="space-y-4 lg:space-y-6">
      <div className="rounded-lg border border-border bg-surface">
        <header className="px-4 sm:px-5 py-3 border-b border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-text">Eligible contacts — {customerName}</h3>
            <p className="text-xs text-text-muted">
              Contacts synced from Zoho. Primary contact by default; opt in additional contacts to invite them.
            </p>
          </div>
          <label className="flex items-center gap-2 text-xs text-text-muted min-h-[44px] sm:min-h-0">
            <input
              type="checkbox"
              checked={showAllContacts}
              onChange={(e) => setShowAllContacts(e.target.checked)}
              className="rounded border-border"
            />
            Show all contacts
          </label>
        </header>
        {contactsLoading ? (
          <div className="p-5 text-sm text-text-muted">Loading…</div>
        ) : contactsError ? (
          <p role="alert" className="p-5 text-sm text-notification">Could not load contacts. Refresh to try again.</p>
        ) : eligible.length === 0 ? (
          <div className="p-6 text-center text-sm text-text-muted">
            {contactTotal === 0
              ? 'No Zoho contacts are synced for this customer yet. Run a Contacts pull on the Zoho Sync page, then invite from here.'
              : showAllContacts
                ? 'Every synced contact already has an access record. Review current status on Portal Access.'
                : 'No primary contact in Zoho. Turn on "Show all contacts" to invite another contact, or set the primary in Zoho first.'}
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {eligible.map((c) => (
              <EligibleContactRow
                key={c.id}
                contact={c}
                canManage={canManage}
                customerId={customerId}
                onInvite={(params) => invite.mutateAsync(params)}
              />
            ))}
          </ul>
        )}
      </div>

      <div className="rounded-lg border border-border bg-surface">
        <header className="px-4 sm:px-5 py-3 border-b border-border flex items-center justify-between">
          <h3 className="text-sm font-semibold text-text">Portal access roster</h3>
          <span className="text-xs text-text-muted">
            {users?.length ?? 0} {(users?.length ?? 0) === 1 ? 'login' : 'logins'}
          </span>
        </header>
        {isLoading ? (
          <div className="p-5 text-sm text-text-muted">Loading…</div>
        ) : isError ? (
          <p role="alert" className="p-5 text-sm text-notification">Could not load the roster. Refresh to try again.</p>
        ) : users && users.length > 0 ? (
          <>
            {/* Mobile cards */}
            <ul className="md:hidden divide-y divide-border">
              {users.map((u) => (
                <li key={u.id} className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-text truncate">{u.user.full_name ?? u.user.email}</p>
                      <p className="text-xs text-text-muted break-all">{u.user.email}</p>
                    </div>
                    <StatusPill status={u.status} />
                  </div>
                  <p className="text-xs text-text-muted">
                    {u.role}
                    {u.is_primary_contact ? ' · primary contact' : ''} · Last login {u.last_login_at ? formatDateTime(u.last_login_at) : 'never'}
                  </p>
                  <OpenPortalLink userId={u.user_id} email={u.user.email} />
                </li>
              ))}
            </ul>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-surface-3 text-xs text-text-muted">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Email</th>
                    <th className="text-left px-4 py-2 font-medium">Role</th>
                    <th className="text-left px-4 py-2 font-medium">Status</th>
                    <th className="text-left px-4 py-2 font-medium">Last login</th>
                    <th className="text-right px-4 py-2 font-medium">Portal</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {users.map((u) => (
                    <tr key={u.id}>
                      <td className="px-4 py-2 text-text">
                        {u.user.email}
                        {u.is_primary_contact && (
                          <span className="ml-2 rounded bg-primary-soft px-1.5 py-0.5 text-xs text-primary">Primary</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-text">{u.role}</td>
                      <td className="px-4 py-2"><StatusPill status={u.status} /></td>
                      <td className="px-4 py-2 text-text-muted text-xs whitespace-nowrap">
                        {u.last_login_at ? formatDateTime(u.last_login_at) : 'Never'}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <OpenPortalLink userId={u.user_id} email={u.user.email} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="p-6 text-center text-sm text-text-muted">
            No portal access yet. Pick a contact above to send the first invite.
          </div>
        )}
      </div>
    </section>
  );
}

function OpenPortalLink({ userId, email }: { userId: string; email: string }) {
  return (
    <a
      href={portalViewAsUrl(userId)}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Open the customer portal as ${email}`}
      className="inline-flex items-center justify-center min-h-[44px] md:min-h-[32px] rounded-md border border-border bg-surface px-2.5 text-xs font-medium text-text hover:bg-surface-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      Open portal as this user
    </a>
  );
}

function EligibleContactRow({
  contact,
  customerId,
  onInvite,
  canManage,
}: {
  contact: ContactLite;
  canManage: boolean;
  customerId: string;
  onInvite: (params: InviteParams) => Promise<{ runId: string | null }>;
}) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const send = async () => {
    if (!canManage || reason.trim().length < 5) return;
    if (!contact.email) {
      setError('This contact has no email address in Zoho.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await onInvite({
        customer_id: customerId,
        reason,
        email: contact.email,
        full_name: contact.full_name ?? undefined,
        role: 'viewer',
        is_primary_contact: contact.is_primary,
        contact_id: contact.id,
        zoho_contact_id: contact.zoho_contact_id ?? undefined,
      });
      setDone(result.runId ? `Invite queued (run ${result.runId}).` : 'Invite queued.');
    } catch (e) {
      setError(describeJobError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="px-4 sm:px-5 py-3">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm text-text font-medium truncate">
            {contact.full_name ?? contact.email ?? 'Unnamed contact'}
            {contact.is_primary && (
              <span className="ml-2 rounded bg-primary-soft px-1.5 py-0.5 text-xs text-primary">primary</span>
            )}
          </div>
          <div className="text-xs text-text-muted break-all">
            {contact.email ?? 'No email in Zoho'}
            {contact.title && <span className="ml-2">· {contact.title}</span>}
          </div>
        </div>
        {canManage && <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-text-muted">Invite as viewer</span>
          <button
            type="button"
            onClick={send}
            disabled={busy || Boolean(done) || !contact.email || reason.trim().length < 5}
            className="inline-flex items-center justify-center min-h-[44px] sm:min-h-[36px] rounded-md bg-primary hover:bg-primary-hover disabled:opacity-50 px-3 text-xs font-medium text-text-inverse focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            {done ? 'Queued' : busy ? 'Sending…' : 'Invite'}
          </button>
        </div>}
      </div>
      {canManage && !done && <label className="mt-2 block text-xs text-text-muted">Reason for invitation (5–1000 characters)
        <textarea value={reason} onChange={(event) => setReason(event.target.value)} maxLength={1000} rows={2} disabled={busy} className="mt-1 w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text" />
      </label>}
      {!canManage && <p className="text-xs text-text-muted">Access changes require an admin or superadmin.</p>}
      {done && <p role="status" className="mt-1.5 text-xs text-success break-words">{done}</p>}
      {error && <p role="alert" className="mt-1.5 text-xs text-error break-words">{error}</p>}
    </li>
  );
}

function StatusPill({ status }: { status: CustomerUserStatus }) {
  const styles: Record<CustomerUserStatus, string> = {
    invited: 'bg-warning-soft text-warning',
    active: 'bg-success-soft text-success',
    disabled: 'bg-surface-3 text-text-muted',
    revoked: 'bg-error-soft text-error',
  };
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${styles[status]}`}>
      {status}
    </span>
  );
}
